<script setup lang="ts">
//#region imports
import QS from 'qs';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format, startOfDay, subDays } from 'date-fns';
import ExportData from '@/components/ExportData.vue';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { useLs } from '@/setup/vueLs';
import { useEventBus } from '@/setup/eventBus';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useTableFilter, useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useTableColumnWidths } from '@/composables/useTableColumnWidths';
import { usePopupManager } from '@/components/PopupManager.vue';
import PlayerFilter from './components/PlayerFilter.vue';
import type { GetPlayersResponse, GetPlayersData } from '@/types/openapi-ts';
import type { PlayerFilters } from './components/PlayerFilter.vue';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
import type { SettingsPageQuery } from '@/types/core';

//#endregion

//#region types
type PlayerRow = GetPlayersResponse['payload'][number];
type PlayersListQuery = SettingsPageQuery<GetPlayersData['query']>;

type ExportUrls = {
  csv: string;
  xlsx: string;
} | null;


type ModifiedServerResponse = Omit<GetPlayersResponse, 'misc'> & {
  misc?: (NonNullable<GetPlayersResponse['misc']> & {
    export_urls: ExportUrls;
  });
};

type PlayersList = {
  data: ModifiedServerResponse;
};
//#endregion

//#region const
const LS_PATH = 'PlayersList';
const LS_SITE_ID = `${LS_PATH}/siteId`;
const LS_COLUMN_WIDTHS = `${LS_PATH}/width-column`;

const sizes = {
  1080: 20,
  1440: 30,
};


const configExport = [
  {
    command: 'csv',
    label: 'CSV',
  },
  {
    command: 'xlsx',
    label: 'XLSX',
  },
];

const playerListFields: UiTableNewField<PlayerRow>[] = [
  {
    name: 'site_player_id',
    sortable: 'custom',
    link: getPlayerLink,
    width: '160',
    minWidth: '70',
  },
  {
    name: 'affiliate_email',
    minWidth: '200',
    width: '200',
    class: 't:!justify-start',
    link: getAffiliateLink,
  },
  {
    name: 'player_country_name',
    width: '170',
    minWidth: '170',
  },
  {
    name: 'phone_number',
    minWidth: '170',
    width: '170',
  },
  {
    name: 'email',
    minWidth: '100',
    flexible: true,
  },
  {
    name: 'first_name',
    minWidth: '100',
    flexible: true,
    computedValue(row) {
      return `${row.first_name || ''} ${row.last_name || ''}`;
    },
  },
  {
    name: 'is_virtual',
    width: '100',
    minWidth: '100',
    resizable: false,
    class: 'actions',
    action: (row) => {
      if (!row.is_virtual && editVirtual.value) {
        openMarkPlayerVirtual(row);
      }
    },
    actionName: (row) => (
      !row.is_virtual && editVirtual.value ? 'pen' : ''
    ),
    controlType: 'icon',
    controlColorIco() {
      return 'var(--text-positive)';
    },
    controlColor(row) {
      return row.is_virtual ? 'var(--text-positive)' : 'var(--text-body)';
    },
    computedValue: (row) => (
      row.is_virtual
        ? t('players.playersList.yes')
        : t('players.playersList.no')
    ),
  },
  {
    name: 'created_at',
    minWidth: '120',
    width: '120',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const { t } = useI18n();
const ls = useLs();
const eventBus = useEventBus();
const { checkRequest, checkRoute } = useDetectPermissions();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort, setSort } = useTableSort({
  lsPath: LS_PATH,
  fields: playerListFields,
  initialOptions: {
    sort_column: 'created_at',
    sort_dir: 'desc',
  },
});

const { filterParams, setFilter } = useTableFilter({
  lsPath: LS_PATH,
});

const { searchQuery, setSearch } = useTableSearch();
const popupManager = usePopupManager();
const updateUrl = useUpdateUrl();
const { fields, headerDragend } = useTableColumnWidths({
  lsKey: LS_COLUMN_WIDTHS,
  fields: playerListFields,
});
//#endregion

//#region state
const currentBrand = ref('');
const isCompleteRequest = ref(false);
const exportUrls = ref<ExportUrls>(null);

const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<GetPlayersResponse['payload']>([]);
//#endregion

//#region computed
const isSuperuser = computed(() => store.getters['auth/adminAcl'].is_superuser);
const brands = computed(() => store.getters['misc/brands']);

const editVirtual = computed(() => calcPermissions('players_virtual_edit_settings'));
const editAssignAffiliate = computed(() => calcPermissions('players_assign_affiliate'));
const isEmptyFilter = computed(() => _.isEmpty(filterParams.value) && (searchQuery.value === '' || searchQuery.value === null));
const isExportDisabled = computed(() => !(_.has(filterParams.value, 'player_country_code') || _.has(filterParams.value, 'affiliate_id')));

const brandLogo = computed(() => {
  return brands.value.find((brand) => brand.id === currentBrand.value)
    ?.favicon_base64 ?? false;
});

const playersAvailableKeys = computed(() => {
  return store.getters['auth/currentAcl']
    ?.players_available_properties as string[] | undefined;
});

const fieldsFiltered = computed(() => {
  const availableProperties = playersAvailableKeys.value;
  if (availableProperties) {
    return fields.value.filter((item) => {
      return availableProperties.includes(item.name) || item.name === 'site_player_id'
        || ( availableProperties.includes('last_name') && item.name === 'first_name');
    });
  }
  if (isSuperuser.value) {
    return fields.value;
  }
  return [];
});

const emptyTableState = computed(() => {
  if(isEmptyFilter.value) {
    return {
      icon: 'filter',
      text : t('players.playersList.select_filter'),
    };
  }

  return {
    icon: 'folder-open',
    text : t('ui.table.no_data'),
  };
});
//#endregion

//#region methods
function initData() {
  if (checkRequest('players_view')){
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);
      setFilter(_urlData);

      delete _urlData.page;
      delete _urlData.limit;
      delete _urlData.filters;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      setSearch(_urlData);
    }

    currentBrand.value = (_urlData?.site_id as string) || ls.get(LS_SITE_ID) || brands.value[0]?.id;

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('players_view');
}

const _query = () => {
  return {
    ...optionsSort.value,
    sort_column: getSortBy(sortProp.value),
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    site_id: currentBrand.value,
  } as GetPlayersData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters: PlayersListQuery =_.omit(_query(), 'offset');
  _dataFilters.page = page.value;
  _dataFilters.filters = JSON.stringify(filterParams.value);
  if(Object.keys(filterParams.value).length <= 0) {
    delete _dataFilters.filters;
  }
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!isEmptyFilter.value) {
    if (!silent && !isFirstLoading.value) {
      isDataLoading.value = true;
    }

    _completedFilterUrl();
    let options = _query();
    options = {
      ...options,
      ...transformFiltersToQuery(filterParams.value),
    };

    try {
      const { data: { payload, misc } } = (await api.getPlayers(options)) as unknown as PlayersList;

      count.value = misc!.count;
      tableData.value = payload;
      exportUrls.value = misc!.export_urls;
    } finally {
      if(!isFirstLoading.value) {
        isDataLoading.value = false;
      } else {
        isFirstLoading.value = false;
      }

      isCompleteRequest.value = true;
    }
  } else {
    _completedFilterUrl();
    tableData.value = [];
    isCompleteRequest.value = false;
  }
}

function transformFiltersToQuery(nv: PlayerFilters) {
  const nextQuery = _.clone(nv) as Record<string, unknown>;

  if (_.has(nv, 'deposits')) {
    if (nv.deposits === 'without') {
      nextQuery.max_deposits_count = 0;
      nextQuery.min_deposits_count = 0;
      delete nextQuery.deposits;
    }
    if (nv.deposits === 'first') {
      nextQuery.max_deposits_count = 1;
      nextQuery.min_deposits_count = 1;
      delete nextQuery.deposits;
    }
    if (nv.deposits === 'more') {
      nextQuery.min_deposits_count = 2;
      delete nextQuery.deposits;
    }
    if (nv.deposits === 'onlydep') {
      nextQuery.min_deposits_count = 1;
      delete nextQuery.deposits;
    }
  }

  if (_.has(nv, 'contacts')) {
    if (nv.contacts === 'only') {
      nextQuery.only_with_contacts = true;
      delete nextQuery.contacts;
    }
  }

  if (_.has(nv, 'activity')) {
    const days = Number(nv.activity?.[0]);
    const dir = nv.activity?.[1];

    if (Number.isFinite(days) && dir) {
      const date = format(
        startOfDay(subDays(new Date(), days)),
        'yyyy-MM-dd HH:mm:ss',
      );

      if (dir === 'from') {
        nextQuery.last_active_at_from = date;
      }
      if (dir === 'to') {
        nextQuery.last_active_at_to = date;
      }
    }
    delete nextQuery.activity;
  }

  return nextQuery;
}

function calcPermissions(item: string) {
  const currentAcl = store.getters['auth/currentAcl'];

  return currentAcl[item as keyof typeof currentAcl] === 'allow'
    || (
      isSuperuser.value
      && !localStorage.getItem('active-team')
    );
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function getSortBy(key: string) {
  if (isSuperuser.value || playersAvailableKeys.value?.includes(key)) {
    return key;
  }
  return 'site_player_id';
}

function handleOpenTransferPopup() {
  popupManager.transferPlayers.open({
    data: { siteId: currentBrand.value },
    listeners: { success: () => fetchData(true) },
  });
}

function openLinkAffiliate(row?: unknown) {
  popupManager.linkAffiliate.open({
    data: { player: (row || {}) as Partial<PlayerRow>, siteId: currentBrand.value },
    listeners: {
      success: () => fetchData(),
    },
  });
}

function openMarkPlayerVirtual(value: PlayerRow) {
  popupManager.markPlayerVirtual.open({
    data: {
      playerId: value.id,
      sitePlayerId: value.site_player_id,
      isVirtual: value.is_virtual,
    },
    listeners: {
      success: () => fetchData(),
    },
  });
}

function exportPlayers(exportFormat: 'csv' | 'xlsx') {
  const urls = exportUrls.value;

  if (urls && urls[exportFormat]) {
    window.open(urls[exportFormat]);
  }
}

function getPlayerLink(row: PlayerRow) {
  const urlObj = {
    back: window.location.pathname + window.location.search,
  };
  return `/players/${row.id}/info?param_playerlist=${QS.stringify(urlObj)}`;
}

function getAffiliateLink(row: PlayerRow) {
  if (row.affiliate_referred_by_info) {
    return row.affiliate_id && row.affiliate_referred_by_info.permissions.view
      ? `/affiliates/${row.affiliate_id}/info`
      : '';
  }
  return row.affiliate_id ? `/affiliates/${row.affiliate_id}/info` : '';
}

function checkSensitiveAction(row: PlayerRow) {
  eventBus.$emit('checkSensitiveAction', 'clickEditAffiliateHandler', row);
}

function checkSensitiveMassTransferAction() {
  eventBus.$emit('checkSensitiveAction', 'handleOpenTransferPopup');
}

function changeLocaleGlobal() {
  fetchData(true);
}

function changeBrand(brand: string) {
  currentBrand.value = brand;
  ls.set(LS_SITE_ID, brand);

  handleRefresh();
}

function getPlayersFilter(data: PlayerFilters) {
  setFilter({filters: data}, {}, false);
  handleRefresh();
}
//#endregion

//#region hooks
initData();

onMounted(() => {
  eventBus.$on('changeLocaleGlobal', changeLocaleGlobal);
  eventBus.$on('clickEditAffiliateHandler', openLinkAffiliate);
  eventBus.$on('handleOpenTransferPopup', handleOpenTransferPopup);
});

onBeforeUnmount(() => {
  eventBus.$off('changeLocaleGlobal', changeLocaleGlobal);
  eventBus.$off('clickEditAffiliateHandler', openLinkAffiliate);
  eventBus.$off('handleOpenTransferPopup', handleOpenTransferPopup);
});
//#endregion
</script>

<template>
  <div class="players-list">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('players.playersList.players') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <UiSelectElement
          v-model="currentBrand"
          filterable
          :list="brands"
          :select-item-logo="brandLogo"
          list-item-logo="favicon_base64"
          width="200"
          @change="changeBrand"
        />
        <PlayerFilter
          :model-value="filterParams"
          @submit="getPlayersFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          v-if="!isEmptyFilter && isCompleteRequest"
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="editAssignAffiliate"
          is-icon
          icon="right-left"
          @click="checkSensitiveMassTransferAction"
        />
        <ExportData
          :data="configExport"
          :disabled="isExportDisabled"
          @export-data="exportPlayers"
        />
        <UiButton
          icon="sync-alt"
          @click="fetchData()"
        >
          {{ $t('players.playersList.refresh') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :lazy-loading="!isEmptyFilter"
      :fields="fieldsFiltered"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      :empty-icon-name="emptyTableState.icon"
      :empty-text="emptyTableState.text"
      i18n-path="players"
      element-loading-custom-class="data-loader-spinner"
      is-static
      @sort="handleSort"
      @header-dragend="headerDragend"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="80"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('crm.buttons.view')"
              :router-to="getPlayerLink(scope.row)"
            />
            <ActionIcon
              :icon="scope.row.affiliate_id ? 'unlink' : 'link'"
              :tooltip="$t('players.pinPlayer.tooltip')"
              :color="scope.row.affiliate_id ? 'var(--text-negative)' : 'var(--text-positive)'"
              @click="checkSensitiveAction(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
